<template>
  <div
    @click="$emit('click', result)"
    :class="[BG_COLORS.hover, 'w-full px-3 py-2 cursor-pointer transition-colors duration-200 border-b border-gray-100 dark:border-gray-700']"
    role="button"
    :aria-label="`View ${result.title} ${typeLabel}`"
  >
    <div class="flex items-center justify-between">
      <HLSpace vertical :size="8" class="flex-1 min-w-0">
        <div class="flex items-center space-x-1.5">
          <HLText id="search-result-title" size="2xl" weight="semibold" html-tag="span" class="truncate">
            {{ result.title }}
          </HLText>
          <!-- Type Tag -->
          <HLTag 
            :id="`search-type-${result.id}`"
            :color="typeTagConfig.color" 
            size="xs"
            :bordered="false"
            round
            class="flex-shrink-0"
          >
            {{ typeLabel }}
          </HLTag>
        </div>
        <p v-if="result.description" class="text-sm text-gray-500 dark:text-gray-400 truncate text-left">
          {{ result.description }}
        </p>
        <HLSpace :size="12" align="center">
          <span class="text-xs text-gray-400 dark:text-gray-500">
            {{ result.parentInfo }}
          </span>
          <HLTag 
            v-if="result.status && statusTagConfig" 
            :id="`search-status-${result.id}`"
            :color="statusTagConfig.color" 
            size="xs"
            :bordered="false"
            round
          >
            {{ formatStatus(result.status) }}
          </HLTag>
        </HLSpace>
      </HLSpace>
      <div class="flex-shrink-0 ml-2">
        <HLIcon id="search-result-chevron" size="14" color="#9CA3AF" aria-hidden="true">
          <ChevronRightIcon />
        </HLIcon>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { HLTag, HLSpace, HLIcon, HLText } from '@platform-ui/highrise'
import { ChevronRightIcon } from '@gohighlevel/ghl-icons/24/outline'
import { 
  getSearchTypeHLTag, 
  formatStatus,
  getProjectStatusHLTag,
  getFeatureStatusHLTag,
  getBugStatusHLTag,
  getTestCaseStatusHLTag,
  getTestRunStatusHLTag
} from '@/utils/badgeHelpers'
import { SEARCH_TYPE_LABELS } from '@/constants/dashboard'
import { BG_COLORS } from '@/constants/uiColors'
import { t } from '@/i18n/en-us'

const props = defineProps({
  result: {
    type: Object,
    required: true,
    validator: (value) => {
      return value.id && value.type && value.title
    }
  }
})

defineEmits(['click'])

const typeTagConfig = computed(() => getSearchTypeHLTag(props.result.type))

const typeLabel = computed(() => SEARCH_TYPE_LABELS[props.result.type] || t('dashboard.unknownType'))

const statusTagConfig = computed(() => {
  if (!props.result.status) return null
  
  const type = props.result.type?.toLowerCase()
  
  switch (type) {
    case 'project':
      return getProjectStatusHLTag(props.result.status)
    case 'feature':
      return getFeatureStatusHLTag(props.result.status)
    case 'bug':
      return getBugStatusHLTag(props.result.status)
    case 'testcase':
      return getTestCaseStatusHLTag(props.result.status)
    case 'testrun':
      return getTestRunStatusHLTag(props.result.status)
    default:
      return null
  }
})
</script>

